import { useCallback, useEffect, useState } from 'react';

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

interface UseRecaptchaOptions {
  siteKey: string;
  action: string;
}

interface UseRecaptchaReturn {
  executeRecaptcha: () => Promise<string>;
  isRecaptchaLoaded: boolean;
  recaptchaError: string | null;
}

/**
 * React hook to load the reCAPTCHA script if the script was not already loaded (eg. by Gravity Forms).
 */
export function useRecaptcha({ siteKey, action }: UseRecaptchaOptions): UseRecaptchaReturn {
  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null);

  const isRecaptchaEnabled = process.env.REACT_APP_RECAPTCHA_ENABLED !== 'false';

  console.log('useRecaptcha', {
    isRecaptchaLoaded: isRecaptchaLoaded,
    recaptchaError: recaptchaError,
    isRecaptchaEnabled: isRecaptchaEnabled,
  });

  useEffect(() => {
    console.log('useRecaptcha::useEffect 1');

    if (!isRecaptchaEnabled) {
      setIsRecaptchaLoaded(true);
      return;
    }

    console.log('useRecaptcha::useEffect 2');

    // check if reCAPTCHA script is already loaded on the page
    const existingScript = document.querySelector(
      'script[src*="recaptcha/api.js"], script[src*="recaptcha"], script[src*="grecaptcha"]'
    );

    if (existingScript) {
      console.log('useRecaptcha::useEffect existingScript=true');

      if (window.grecaptcha) {
        setIsRecaptchaLoaded(true);
        return;
      }

      console.log('useRecaptcha::useEffect window.grecaptcha=falsy');

      // if script exists but grecaptcha isn't ready yet, wait for it
      const checkGrecaptcha = () => {
        if (window.grecaptcha) {
          window.grecaptcha.ready(() => {
            console.log('useRecaptcha::useEffect::checkGrecaptcha window.grecaptcha.ready');
            setIsRecaptchaLoaded(true);
            setRecaptchaError(null);
          });
        } else {
          // Check again in a short time
          setTimeout(checkGrecaptcha, 500);
        }
      };

      checkGrecaptcha();
      return;
    }

    console.log('useRecaptcha::useEffect existingScript=false');

    // load reCAPTCHA script if not already loaded
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      window.grecaptcha.ready(() => {
        setIsRecaptchaLoaded(true);
        setRecaptchaError(null);
      });
    };

    script.onerror = () => {
      setRecaptchaError('Failed to load reCAPTCHA script');
    };

    document.head.appendChild(script);

    // don't cleanup since we don't want to remove scripts that might be used in Gravity Forms
  }, [siteKey, isRecaptchaEnabled]);

  const executeRecaptcha = useCallback(async (): Promise<string> => {
    // if reCAPTCHA is disabled, return a dummy token
    if (!isRecaptchaEnabled) {
      return 'recaptcha-disabled';
    }

    // if reCAPTCHA is not loaded, try to wait for it
    if (!window.grecaptcha) {
      await new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 60; // 30 seconds max wait

        const checkGrecaptcha = () => {
          if (window.grecaptcha) {
            resolve(true);
          } else if (attempts >= maxAttempts) {
            // reject(new Error('reCAPTCHA failed to load within timeout'));
            reject({ recaptcha: 'reCAPTCHA failed to load within timeout' });
          } else {
            attempts++;
            setTimeout(checkGrecaptcha, 500);
          }
        };

        checkGrecaptcha();
      });
    }

    try {
      const token = await window.grecaptcha.execute(siteKey, { action });
      return token;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to execute reCAPTCHA';
      setRecaptchaError(errorMessage);
      // throw new Error(errorMessage);
      // eslint-disable-next-line no-throw-literal
      throw { recaptcha: errorMessage };
    }
  }, [siteKey, action, isRecaptchaEnabled]);

  return {
    executeRecaptcha,
    isRecaptchaLoaded,
    recaptchaError,
  };
}
